import {PropsWithChildren, useEffect, useState, useMemo} from 'react';
import {useLocation} from 'react-router';

import {useRunValidationsOnDocumentModelChange} from '@/components/document/model/use-run-validations-on-document-model-change';
import {SIGNATURE_INFO_SECTION} from '@/constants/sections';
import {getInitialSection} from '@/utils/document/get-initial-section';
import {getNextSectionId} from '@/utils/document/validation';
import {useDocumentHasUnsavedChanges} from '../hooks/use-document-has-changed';
import {useDocumentModel} from '../model/use-document-model';
import {useDocumentRepository} from '../repository/use-document-repository';
import {useDocumentUtils} from '../utils/use-document-utils';
import {useDocumentActions} from './actions/use-document-actions';
import {DocumentContext, DocumentContextType} from './document-context';
import {useOnDocumentLoaded} from './events/use-on-document-loaded';
import {useOnDocumentModelChange} from './events/use-on-document-model-change';
import {useSectionRemountKey} from './use-section-remount-key';

export const DocumentContextProvider = ({children}: PropsWithChildren) => {
  const isResendingDocument = useLocation().state?.isResendingDocument;

  const {isSaving, hasSavingError, isSubmitting, document} = useDocumentRepository();

  const utils = useDocumentUtils();

  const {ready, documentModel, visibilitySet} = useDocumentModel({
    onChanged: useOnDocumentModelChange(),
  });

  const hasUnsavedChanges = useDocumentHasUnsavedChanges({data: utils.store.getState(), visibilitySet});

  const visibleSections = useMemo(
    () => documentModel.sections.filter(section => !section.hidden),
    [documentModel.sections],
  );

  const [currentSectionId, setCurrentSectionId] = useState(() =>
    getInitialSection(documentModel, utils.store.getInitialState(), utils.valueResolver),
  );

  const {sectionRemountKey, markCurrentSectionAsCleared} = useSectionRemountKey();

  const activeSectionId = isResendingDocument ? SIGNATURE_INFO_SECTION : currentSectionId;

  const {currentSectionIndex, currentSection, nextSection} = useMemo(() => {
    const currentSectionIndex = visibleSections.findIndex(section => section.id === activeSectionId);
    const currentSection = currentSectionIndex !== -1 ? visibleSections[currentSectionIndex] : null;

    const nextSectionId = getNextSectionId(documentModel, activeSectionId);
    const nextSection =
      (!!nextSectionId && documentModel.sections.find(section => section.id === nextSectionId)) || null;

    return {currentSectionIndex, currentSection, nextSection};
  }, [activeSectionId, visibleSections, documentModel]);

  const [forms, setForms] = useState(() => document.forms ?? []);

  const onDocumentLoaded = useOnDocumentLoaded({
    visibilitySet,
    isResendingDocument,
  });

  useEffect(() => {
    onDocumentLoaded();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- should only run once
  }, []);

  useRunValidationsOnDocumentModelChange(activeSectionId, documentModel, visibilitySet);

  const actions = useDocumentActions({
    formSectionId: activeSectionId,
    nextSectionId: nextSection?.id ?? null,
    documentModel,
    setCurrentSectionId,
    markCurrentSectionAsCleared,
    visibilitySet,
  });

  const value: DocumentContextType = {
    ready,
    visibleSections,
    currentSectionId: activeSectionId,
    currentSectionIndex,
    currentSection,
    nextSection,
    setCurrentSectionId,
    sectionRemountKey,
    document,
    forms,
    setForms,
    isSaving,
    hasSavingError,
    isSubmitting,
    documentModel,
    hasUnsavedChanges,
    visibilitySet,
    ...utils,
    ...actions,
  };

  return <DocumentContext.Provider value={value}>{children}</DocumentContext.Provider>;
};
